import { useUser } from "@auth";
import { useMerchant } from "@auth";
import {
  Button,
  Flow,
  FlowActions,
  FlowHeader,
  FlowTitle,
  FormError,
  FormGroup,
  FormLabel,
  RadioCard,
  RadioGroup,
  Tab,
  TabGroup,
  TextInput,
  Token,
  RequestSmsPreview,
  RequestEmailPreview,
  TemplateTextarea,
} from "@components";
import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { templateTokens, replaceTokens } from "@utils";
import { createMessageTemplate } from "@api";
//import OntikPaymentRequestQRModal from "./OntikPaymentRequestQRModal";
//import RequestEmailPreview from "../molecules/RequestEmailPreview";
//import RequestSmsPreview from "../molecules/RequestSmsPreview";

const CreateTemplate = ({ isOpen, onComplete, onClose }) => {
  const { user } = useUser();
  const { merchant } = useMerchant();
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState("sms");

  // We'll track which field is currently in focus:
  const [focusedField, setFocusedField] = useState(null);
  // We'll store the selection (cursor) positions for each field separately:
  const [selections, setSelections] = useState({
    emailBody: { start: 0, end: 0 },
    smsMessage: { start: 0, end: 0 },
  });

  const emailBodyRef = useRef(null);
  const smsMessageRef = useRef(null);

  const getSmsTemplate = () => {
    return `Dear ${templateTokens[0].value}, please make a payment of ${templateTokens[1].value} to ${merchant?.name || "us"} using the link below.`;
  };

  const getEmailTemplate = () => {
    return `Dear ${templateTokens[0].value},\n\nPlease make a payment of ${templateTokens[1].value} using the link below.\n\nKind regards,\n\n${merchant?.name || "us"}`;
  };

  const {
    control,
    setValue,
    reset,
    getValues,
    watch,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      emailSubject: "Please make your payment",
      emailBody: getEmailTemplate(),
      smsMessage: getSmsTemplate(),
      emailType: "html",
    },
  });

  const onSubmit = async (values) => {
    setSubmitting(true);

    let data = await createMessageTemplate(
      values.name,
      values.emailBody,
      values.emailSubject,
      values.smsMessage,
      values.emailType === "plain",
    );

    reset({
      name: "",
      emailSubject: "Please make your payment",
      emailBody: getEmailTemplate(),
      smsMessage: getSmsTemplate(),
      emailType: "html",
    });

    if (onComplete) {
      onComplete();
    }

    setSubmitting(false);
  };

  const insertToken = (token) => {
    if (!focusedField) return;
    // current value from the form
    const currentValue = getValues(focusedField);

    // current selection
    const { start, end } = selections[focusedField];

    // Insert token into the current value
    const newValue =
      currentValue.slice(0, start) + token + currentValue.slice(end);

    // Update the field via react-hook-form
    setValue(focusedField, newValue);

    // Optionally, reset the cursor after insertion
    // if your <TextInput> supports a ref (textareaRef.current.setSelectionRange, etc.)
    const newCursorPos = start + token.length;

    requestAnimationFrame(() => {
      if (focusedField === "emailBody" && emailBodyRef.current) {
        emailBodyRef.current.setSelectionRange(newCursorPos, newCursorPos);
        emailBodyRef.current.focus();
      } else if (focusedField === "smsMessage" && smsMessageRef.current) {
        smsMessageRef.current.setSelectionRange(newCursorPos, newCursorPos);
        smsMessageRef.current.focus();
      }
    });
  };

  const handleSelectionChange = (fieldName, e) => {
    setFocusedField(fieldName);
    setSelections((prev) => ({
      ...prev,
      [fieldName]: {
        start: e.target.selectionStart,
        end: e.target.selectionEnd,
      },
    }));
  };

  const close = () => {
    reset({
      name: "",
      emailSubject: "Please make your payment",
      emailBody: getEmailTemplate(),
      smsMessage: getSmsTemplate(),
      emailType: "html",
    });

    onClose();
  };

  return (
    <Flow isOpen={isOpen} onClose={close}>
      <FlowHeader onClose={close}>
        <FlowTitle>Create template</FlowTitle>
        <FlowActions>
          <Button
            color="primary"
            type="submit"
            onClick={handleSubmit(onSubmit)}
          >
            Create template
          </Button>
        </FlowActions>
      </FlowHeader>
      <div className="flex w-full grow">
        <form className="p-5 flex flex-col">
          <FormGroup>
            <FormLabel>Template name</FormLabel>
            <TextInput
              name="name"
              displayName="Template name"
              control={control}
              required={true}
              placeholder="Enter the name for this batch..."
              errors={errors.name}
            />
            <FormError name="name" errors={errors} />
          </FormGroup>
          <FormGroup>
            <FormLabel>Email type</FormLabel>
            <RadioGroup>
              <RadioCard
                title="Branded"
                checked={watch("emailType") === "html"}
                onClick={() => setValue("emailType", "html")}
                description="Email contains merchant branding"
              ></RadioCard>
              <RadioCard
                title="Plain text"
                checked={watch("emailType") === "plain"}
                onClick={() => setValue("emailType", "plain")}
                description="Email appears from personal accounts"
              ></RadioCard>
            </RadioGroup>
          </FormGroup>
          <FormGroup>
            <FormLabel>SMS message template</FormLabel>
            <TemplateTextarea
              name="smsMessage"
              displayName="SMS message template"
              className="min-h-36"
              control={control}
              placeholder="Write your sms body here..."
              invalid={!!errors?.smsMessage}
              required={true}
              innerRef={smsMessageRef}
              onFocus={(e) => handleSelectionChange("smsMessage", e)}
              onSelect={(e) => handleSelectionChange("smsMessage", e)}
            />
            <FormError name="smsMessage" errors={errors} />
          </FormGroup>
          <FormGroup>
            <FormLabel>Email subject</FormLabel>
            <TextInput
              name="emailSubject"
              displayName="Subject"
              control={control}
              required={true}
              errors={errors.emailSubject}
            />
            <FormError name="emailSubject" errors={errors} />
          </FormGroup>
          <FormGroup>
            <FormLabel>Email message template</FormLabel>
            <TemplateTextarea
              name="emailBody"
              displayName="Email template"
              className="min-h-36"
              control={control}
              placeholder="Write your email body here..."
              invalid={!!errors?.emailBody}
              required={true}
              innerRef={emailBodyRef}
              onFocus={(e) => handleSelectionChange("emailBody", e)}
              onSelect={(e) => handleSelectionChange("emailBody", e)}
            />
            <FormError name="emailBody" errors={errors} />
          </FormGroup>
        </form>
        {/* <div className="h-full bg-zinc-50 flex flex-col p-5 gap-3 min-w-[240px] items-start">
          {templateTokens.map((x, i) => (
            <Token key={i} x={x} onClick={insertToken} />
          ))}
        </div> */}
        <div className="h-full flex-1 bg-zinc-100 flex flex-col items-center">
          <div className="w-[390px] max-w-[390px] mx-auto">
            <TabGroup>
              <Tab active={preview === "sms"} onClick={() => setPreview("sms")}>
                SMS
              </Tab>
              <Tab
                active={preview === "email"}
                onClick={() => setPreview("email")}
              >
                Email
              </Tab>
            </TabGroup>
          </div>
          {preview === "sms" && (
            <div className="w-[640px] max-w-[640px] px-auto flex-1 flex flex-col items-center">
              <RequestSmsPreview message={watch("smsMessage")} />
            </div>
          )}
          {preview === "email" && (
            <div className="w-[640px] max-w-[640px] mx-auto flex-1">
              <RequestEmailPreview
                isPlainText={watch("emailType") === "plain"}
                message={watch("emailBody")}
              />
            </div>
          )}
        </div>
      </div>
    </Flow>
  );
};

export default CreateTemplate;
